import dayjs from "dayjs";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { TripType } from "@/features/flights/types/types";
import { initialFlightSearchFormValues } from "@/features/flights/utils/initialFlightsValues";
import {
  DatePicker,
  type DatePickerValue,
} from "@/features/search/components/DatePicker/DatePicker";
import { useDatesGuard } from "@/features/search/hooks/useDatesGuard";
import {
  type SearchSource,
  rudderAnalytics,
  FlightSearchTrackingEvents,
} from "@/lib/rudderstack";
import { getDateString } from "@/utils/dates";

import { TripTypeRadioGroup } from "../components/TripTypeRadioGroup";
import type { FlightSearchForm } from "../FlightSearchForm";

type FlightDatePickerProps = {
  form: FlightSearchForm;
  analyticsSource: SearchSource;
};

export function FlightDatePicker({
  form,
  analyticsSource,
}: FlightDatePickerProps) {
  const intl = useIntl();

  const [tripType, departureDate, returnDate] = useWatch({
    control: form.control,
    name: ["tripType", "dates.departure", "dates.return"],
  });

  const isRange = tripType === TripType.ROUND_TRIP;

  let value: DatePickerValue<boolean> = departureDate;
  if (isRange) {
    value = {
      from: departureDate,
      to: returnDate ?? null,
    };
  }

  const canReset =
    !!departureDate &&
    !dayjs(departureDate).isSame(
      initialFlightSearchFormValues.dates.departure,
      "day",
    );

  useDatesGuard({
    departureDate,
    returnDate: returnDate ?? null,
    setReturnDate: (date) => form.setValue("dates.return", date ?? undefined),
  });

  return (
    <DatePicker
      label={
        isRange
          ? intl.formatMessage({
              defaultMessage: "Departure dates",
              description: "flights.search.departureDates",
            })
          : intl.formatMessage({
              defaultMessage: "Departure date",
              description: "flights.search.departureDate",
            })
      }
      value={value}
      calendarFromLabel={intl.formatMessage({
        defaultMessage: "Departure date",
        description: "flights.search.departureDate",
      })}
      calendarToLabel={intl.formatMessage({
        defaultMessage: "Return date",
        description: "flights.search.returnDate",
      })}
      calendarInitialToValue={returnDate}
      onChange={(values) => {
        if (values instanceof Date || values === null) {
          rudderAnalytics.track(
            FlightSearchTrackingEvents.USER_CHANGED_CALENDAR_DATE,
            {
              type: TripType.ONE_WAY,
              // TODO: Review force non-null assertion
              dateFrom: getDateString(values!),
              source: analyticsSource,
            },
          );

          form.reset({
            ...form.getValues(),
            tripType: TripType.ONE_WAY,
            dates: {
              departure: values ?? undefined,
            },
          });

          return;
        }

        rudderAnalytics.track(
          FlightSearchTrackingEvents.USER_CHANGED_CALENDAR_DATE,
          {
            type: TripType.ROUND_TRIP,
            // TODO: Review force non-null assertion
            dateFrom: getDateString(values.from!),
            dateTo: getDateString(values.to!),
            source: analyticsSource,
          },
        );

        form.reset({
          ...form.getValues(),
          tripType: TripType.ROUND_TRIP,
          dates: {
            departure: values.from ?? undefined,
            return: values.to ?? undefined,
          },
        });
      }}
      isRange={isRange}
      onIsRangeChange={(isRange) => {
        form.setValue(
          "tripType",
          isRange ? TripType.ROUND_TRIP : TripType.ONE_WAY,
        );
      }}
      renderRangePicker={({ value, onChange }) => (
        <TripTypeRadioGroup
          value={value ? TripType.ROUND_TRIP : TripType.ONE_WAY}
          onChange={(e, newValue) => {
            rudderAnalytics.track(
              FlightSearchTrackingEvents.USER_CHANGED_TRIP_TYPE,
              {
                source: `${analyticsSource}_calendar`,
                type: newValue,
              },
            );

            onChange(newValue === TripType.ROUND_TRIP);
          }}
        />
      )}
      onFieldClick={() => {
        rudderAnalytics.track(
          FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_CALENDAR_INPUT,
          { source: analyticsSource },
        );
      }}
      onReset={
        canReset
          ? () => {
              form.setValue(
                "dates.departure",
                initialFlightSearchFormValues.dates.departure,
              );
            }
          : undefined
      }
      calendarProps={{
        allowSameDayRangeSelection: true,
      }}
    />
  );
}
